import { accountsEnvironment } from '../accounts-environment';
import { requiredHeaders } from '../../../models/required-headers';
import { selectData } from '../../../utils/select-data';
import { withJwt } from '../../../utils/with-jwt';
import { companySchema } from '../schemas/company.schema';
import { companyInfoSchema } from '../schemas/company-info.schema';
import { mobileProductSchema } from '../schemas/mobile-product.schema';
import { webProductSchema } from '../schemas/web-product.schema';
import { coercedDate } from '../../../utils/coerced-date';
import { fromFetch } from 'rxjs/fetch';
import { z } from 'zod/v4';

const requestSchema = companySchema
  .omit({
    alias: true,
    products: true,
    purchaseOrderHistory: true,
  })
  .partial()
  .extend({
    companyInfo: companyInfoSchema
      .partial()
      .extend({
        contactName: z.string(),
        email: z.string(),
        phone: z.string(),
      })
      .required({
        contactName: true,
        email: true,
        phone: true,
      }),
    products: z
      .object({
        mobileProducts: z.array(
          mobileProductSchema.omit({ name: true, expirationDate: true })
        ),
        webProducts: z.array(webProductSchema.omit({ name: true })),
      })
      .optional(),
  })
  .required({
    name: true,
    accountType: true,
    companyInfo: true,
  });

const responseSchema = z.union([
  z.object({
    accountId: z.number().min(1),
    alias: z.string().min(1),
    success: z.boolean(),
    createdOn: coercedDate,
  }),
  z.object({
    message: z.string(),
  }),
]);

/**
 * Used by a Trimble Maps partner to create a new account.
 * @param company New account details
 * @returns Basic info for newly created account or just a success message
 *
 * @example
 * AccountsApi.postCompany({
 *  name: 'Account Name',
 *  accountType: 'Paying Customer',
 *  companyInfo: {
 *    contactName: 'Steve',
 *    email: 'email@example.com'
 *    phone: '555-555-5555
 *  }
 * })
 */
export function postCompany(
  this: unknown,
  company: z.infer<typeof requestSchema>
) {
  return withJwt((jwtAuthHeaders) =>
    fromFetch(
      new Request(`${accountsEnvironment.resolve(this).companyUrl}`, {
        method: 'POST',
        headers: {
          ...requiredHeaders,
          ...jwtAuthHeaders,
        },
        body: JSON.stringify(company),
      })
    )
  ).pipe(selectData(responseSchema));
}
